import { useState } from 'react';
import { motion } from 'motion/react';
import { CircleDollarSign, Plus, Pencil, Trash2 } from 'lucide-react';
import { useApp } from '@/store';
import { type ServicePrice } from '@/data/types';
import { useT } from '@/lib/i18n';
import { fmtCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Form = {
  articleNr: string;
  description: string;
  deviceType: string;
  qtyRange: string;
  price: string;
};
const blank = (): Form => ({
  articleNr: '',
  description: '',
  deviceType: '',
  qtyRange: '1',
  price: '0',
});
const fromPrice = (p: ServicePrice): Form => ({
  articleNr: p.articleNr,
  description: p.description,
  deviceType: p.deviceType,
  qtyRange: p.qtyRange,
  price: String(p.price),
});

export function ServicePrices() {
  const t = useT();
  const prices = useApp(s => s.servicePrices);
  const types = useApp(s => s.deviceTypes);
  const settings = useApp(s => s.settings);
  const upsertServicePrice = useApp(s => s.upsertServicePrice);
  const deleteServicePrice = useApp(s => s.deleteServicePrice);
  const toast = useApp(s => s.toast);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ServicePrice | null>(null);
  const [form, setForm] = useState<Form>(blank());

  const openCreate = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };
  const openEdit = (p: ServicePrice) => {
    setEditing(p);
    setForm(fromPrice(p));
    setOpen(true);
  };
  const handleSave = () => {
    if (!form.articleNr.trim() || !form.deviceType) return;
    upsertServicePrice({
      articleNr: form.articleNr.trim(),
      description: form.description.trim(),
      deviceType: form.deviceType,
      qtyRange: form.qtyRange.trim(),
      price: parseFloat(form.price) || 0,
    });
    toast(editing ? t('common.save') : t('prices.newPrice'));
    setOpen(false);
  };
  const handleDelete = (p: ServicePrice) => {
    if (!confirm(`${t('common.delete')} ${p.articleNr}?`)) return;
    deleteServicePrice(p.articleNr);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CircleDollarSign className="h-6 w-6 text-primary" />{' '}
            {t('prices.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('prices.subtitle')}
          </p>
        </div>
        <Button size="sm" onClick={openCreate} data-focus="prices-new">
          <Plus className="h-4 w-4" />
          {t('prices.newPrice')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('prices.priceList')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.articleNr')}</TableHead>
                <TableHead>{t('table.deviceType')}</TableHead>
                <TableHead>{t('table.description')}</TableHead>
                <TableHead>{t('prices.qtyRange')}</TableHead>
                <TableHead className="text-right">{t('table.price')}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {prices.map(p => {
                const dt = types.find(x => x.id === p.deviceType);
                return (
                  <TableRow key={p.articleNr}>
                    <TableCell className="font-mono text-xs">
                      {p.articleNr}
                    </TableCell>
                    <TableCell className="font-medium">
                      {dt?.nameDe ?? p.deviceType}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {p.description}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {p.qtyRange}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmtCurrency(p.price, settings.currency)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 sm:h-7 sm:w-7"
                          onClick={() => openEdit(p)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 sm:h-7 sm:w-7"
                          onClick={() => handleDelete(p)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t('common.edit') : t('prices.newPrice')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <div>
              <Label>{t('table.articleNr')}</Label>
              <Input
                value={form.articleNr}
                onChange={e =>
                  setForm(f => ({ ...f, articleNr: e.target.value }))
                }
                disabled={!!editing}
                className="font-mono"
              />
            </div>
            <div>
              <Label>{t('table.deviceType')}</Label>
              <Select
                value={form.deviceType}
                onValueChange={v => setForm(f => ({ ...f, deviceType: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('table.deviceType')} />
                </SelectTrigger>
                <SelectContent>
                  {types.map(dt => (
                    <SelectItem key={dt.id} value={dt.id}>
                      {dt.nameDe}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>{t('table.description')}</Label>
              <Input
                value={form.description}
                onChange={e =>
                  setForm(f => ({ ...f, description: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('prices.qtyRange')}</Label>
              <Input
                value={form.qtyRange}
                onChange={e =>
                  setForm(f => ({ ...f, qtyRange: e.target.value }))
                }
                placeholder="1-5" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('table.price')}</Label>
              <Input
                type="number"
                step="0.01"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
